import React, { useState } from 'react';
import { LogIn, Link as LinkIcon, CheckCircle, Loader } from 'lucide-react';
import { AxiosError, isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import useLimits from '../plan/hooks/limits';
import { joinMyMeeting } from '../../services/onlineEventService';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export type ResponseCode = 'UrlFormatError' | 'TimeZoneError' | 'Success';

interface MeetingRecorderProps {
  variant?: 'card' | 'inline';
}

const MeetingRecorder: React.FC<MeetingRecorderProps> = ({
  variant = 'card',
}) => {
  const guardLimits = useLimits();
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleRecordMeeting = () => {
    if (!url) {
      setError(t('events.recorder.errors.emptyUrl'));
      return;
    }

    guardLimits('meetingBot', async () => {
      try {
        setLoading(true);
        setError('');
        const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
        const result = await joinMyMeeting(url, timeZone);

        if (result.success) {
          setError('');
          setSuccess(true);
          setUrl('');
        } else if (result?.data?.code === 'UrlFormatError') {
          setError(t('events.recorder.errors.invalidUrl'));
        } else {
          throw new Error('Unable to join the meeting at this time');
        }
      } catch (botError) {
        if (isAxiosError(botError)) {
          const errorObject: AxiosError<{
            data?: { code: ResponseCode };
          }> = botError;
          const code: undefined | ResponseCode =
            errorObject?.response?.data?.data?.code;
          if (code === 'UrlFormatError') {
            setError(t('events.recorder.errors.invalidUrl'));
            return;
          }
        }
        console.error('Unexpected error:', botError?.message);
        setError(t('events.recorder.errors.generic'));
      } finally {
        setLoading(false);
      }
    });
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    if (error) setError('');
    if (success) setSuccess(false);
  };

  const containerClass = variant === 'card' ? 'rounded-lg border bg-card' : '';

  const innerClass = variant === 'card' ? 'p-4' : '';

  return (
    <div className={containerClass}>
      <div className={innerClass}>
        {success ? (
          <div className="flex items-center gap-3 p-2 rounded-lg bg-green-500/10 border border-green-500/20">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                {t('events.recorder.success.title')}
              </p>
              <p className="text-xs text-green-800 dark:text-green-200">
                {t('events.recorder.success.description')}
              </p>
            </div>
          </div>
        ) : (
          <>
            {variant === 'card' && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-1.5">
                  {t('events.recorder.title')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('events.recorder.description')}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('events.recorder.placeholder')}
                  value={url}
                  onChange={handleUrlChange}
                  disabled={loading}
                  className="pl-10"
                />
              </div>
              <Button disabled={loading} onClick={handleRecordMeeting}>
                {loading ? (
                  <Loader className="animate-spin h-4 w-4 text-background" />
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    {t('events.recorder.button')}
                  </>
                )}
              </Button>
            </div>
            {error && <p className="text-xs text-destructive mt-2">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default MeetingRecorder;
